import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { LayoutDashboard, Users2, CreditCard, Coins, Banknote, ScrollText, GitBranch, GraduationCap, Landmark, Settings, ShieldAlert, FileBarChart, Activity, ArrowLeft, LogOut, Menu, ChevronDown } from 'lucide-react';

const GROUPS = [
  { title: null, items: [{ to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true }] },
  { title: 'MEMBERSHIP', items: [
    { to: '/admin/members', label: 'Members', icon: Users2 },
    { to: '/admin/memberships', label: 'Memberships', icon: Users2 },
  ]},
  { title: 'FINANCE', items: [
    { to: '/admin/payments', label: 'Payments', icon: CreditCard },
    { to: '/admin/commissions', label: 'Commissions', icon: GitBranch },
    { to: '/admin/finance', label: 'Allocations & Ledger', icon: Coins },
    { to: '/admin/withdrawals', label: 'Withdrawals', icon: Banknote },
    { to: '/admin/reports', label: 'Reports', icon: FileBarChart },
  ]},
  { title: 'OPERATIONS', items: [
    { to: '/admin/jobs', label: 'Jobs Engine', icon: Activity },
    { to: '/admin/moderation', label: 'Fraud & Audit', icon: ShieldAlert },
  ]},
  { title: 'SYSTEM', items: [
    { to: '/admin/settings', label: 'Business Settings', icon: Settings },
    { to: '/admin/users', label: 'Users & Roles', icon: Users2 },
    { to: '/admin/system', label: 'System Health', icon: Activity },
  ]},
];

const NavContent = ({ onClick }) => {
  const loc = useLocation();
  return (
    <nav className="flex flex-col gap-4">
      {GROUPS.map((g, gi) => (
        <div key={gi}>
          {g.title && <div className="px-3 text-[10px] font-semibold tracking-wider text-white/40 mb-1">{g.title}</div>}
          <div className="flex flex-col gap-0.5">
            {g.items.map((it) => {
              const Icon = it.icon;
              const active = it.end ? loc.pathname === it.to : loc.pathname === it.to || loc.pathname.startsWith(it.to + '/');
              return (
                <Link key={it.to} to={it.to} onClick={onClick} data-testid={`admin-nav-${it.label.split(' ')[0].toLowerCase()}`}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}`}>
                  <Icon className="h-4 w-4" />{it.label}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
};

export const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 flex-col border-r bg-[hsl(var(--brand-ink))] text-white min-h-screen sticky top-0 p-4 overflow-y-auto">
        <div className="flex items-center gap-2 px-2 py-3 mb-2">
          <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-gold))] flex items-center justify-center font-display font-bold text-[hsl(var(--brand-ink))]">G</div>
          <span className="font-display font-bold text-sm">Admin Control</span>
        </div>
        <NavContent />
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="px-3 py-1 text-[11px] text-white/50">{user?.name} · {user?.role}</div>
          <Button variant="ghost" className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate('/dashboard')}><ArrowLeft className="h-4 w-4 mr-2" />Member Area</Button>
          <Button variant="ghost" className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10" onClick={logout}><LogOut className="h-4 w-4 mr-2" />Keluar</Button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="border-b bg-card px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 bg-[hsl(var(--brand-ink))] text-white border-white/10 overflow-y-auto">
                <div className="mt-6"><NavContent onClick={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <h1 className="font-display text-lg sm:text-xl font-bold">{title}</h1>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
